/**
 * TurtleMaterial — the flipper stroke plus this game's underwater lighting.
 *
 * The vertex stage is vendored: world/turtle/voxelTurtle.js ships
 * `TURTLE_STROKE_GLSL`, which rebuilds the blade's frame at each voxel's own station along
 * the wing. That is what lets the tip lag the root, so the whole flipper flexes through the
 * stroke — closed form, per vertex, one draw call.
 *
 * The fragment stage is ours, so the animal is lit by the same sun as the reef it swims over.
 */
import * as THREE from 'three';
import { OCEAN, WATER } from '../data/tuning.ts';
import { COAST_GLSL } from '../world/Coast.ts';
import { TURTLE_STROKE_GLSL } from '../world/turtle/voxelTurtle.js';
import { applyEnv, type SkyEnv } from '../world/DayNight.ts';

function srgb(hex: number): THREE.Color {
  return new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
}

const VERT = /* glsl */ `
${TURTLE_STROKE_GLSL}
${COAST_GLSL}

uniform float uVox;
uniform float uDuneAmp;
uniform float uReefRise;
uniform float uGround;

attribute vec3 aRest;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vCol;
varying float vRole;
varying float vShade;

void main() {
  vec3 centre = aRest;
  if (aFlip >= 0.0) flipperPos(aFlip, aS, aC, aT, aRest, centre);

  vec4 cw = modelMatrix * vec4(centre, 1.0);

  /* Drape, FLIPPERS ONLY. A resting turtle splays its flippers over whatever it is lying on,
     so lifting those voxels clear of the bed is both correct and free. The shell must NOT be
     draped: it is rigid, and lifting its voxels individually would mould a hard carapace to
     the sand. The body's own height clamp keeps the shell out of the ground instead. */
  float lift = aFlip >= 0.0
    ? max(0.0, bedY(cw.xz, uDuneAmp, uReefRise) + uGround - cw.y)
    : 0.0;

  vec4 world = modelMatrix * vec4(position * uVox + centre, 1.0);
  world.y += lift;
  vWorldPos = world.xyz;
  vWorldNormal = normalize(mat3(modelMatrix) * normal);
  vCol = aCol;
  vRole = aRole;
  vShade = aShade;

  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const FRAG = /* glsl */ `
uniform vec3  uSunDir;
uniform vec3  uSunColor;
uniform vec3  uAmbientTop;
uniform vec3  uAmbientBottom;
uniform vec3  uRimColor;
uniform vec3  uFogColor;
uniform float uFogDensity;
uniform float uTime;
uniform float uSeam;
uniform float uStreak;
uniform vec3  uShellA;
uniform vec3  uShellB;
uniform sampler2D uShadowMap;
uniform mat4      uShadowMatrix;
uniform float     uShadowStrength;

varying vec3  vWorldPos;
varying vec3  vWorldNormal;
varying vec3  vCol;
varying float vRole;
varying float vShade;

float lattice(vec2 p) {
  return (sin(p.x) + sin(p.y * 0.87 + 1.7) + sin((p.x + p.y) * 0.62 + 3.1)) * 0.3333333;
}
float dapple(vec2 w, float t) {
  float a = 1.0 - abs(lattice(w * 0.55 + vec2( 0.31, -0.22) * t));
  float b = 1.0 - abs(lattice(w * 1.27 + vec2(-0.19,  0.42) * t));
  return pow(clamp(min(a, b), 0.0, 1.0), 5.0);
}

float shadowAt(vec3 world, vec3 n, float ndl) {
  vec4 lp = uShadowMatrix * vec4(world + n * 0.12, 1.0);
  vec3 c = lp.xyz / lp.w;
  c = c * 0.5 + 0.5;
  if (c.x < 0.0 || c.x > 1.0 || c.y < 0.0 || c.y > 1.0 || c.z > 1.0) return 1.0;
  float bias = mix(0.0018, 0.0004, clamp(ndl, 0.0, 1.0));
  float texel = 1.0 / 2048.0;
  float lit = 0.0;
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel, -texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2(-texel,  texel)).r);
  lit += step(c.z - bias, texture2D(uShadowMap, c.xy + vec2( texel,  texel)).r);
  lit *= 0.25;
  vec2 e = abs(c.xy - 0.5) * 2.0;
  return mix(1.0, lit, 1.0 - smoothstep(0.80, 1.0, max(e.x, e.y)));
}

void main() {
  vec3 N = normalize(vWorldNormal);
  vec3 V = normalize(cameraPosition - vWorldPos);
  vec3 L = normalize(uSunDir);

  vec3 base = vCol;
  if (vRole > 4.5 && vRole < 5.5)      base = vec3(0.020, 0.019, 0.026);   // eye
  else if (vRole > 5.5)                base = vec3(0.700, 0.655, 0.505);   // beak
  else if (vRole > 0.5 && vRole < 1.5) base *= mix(1.0, 0.34, uSeam);      // scute seam
  else if (vRole < 0.5) {
    /* Carapace. The streak is BAKED as a position between the two shell tones, so recovering
       that fraction and re-mixing lets one uniform restate the whole pattern — every
       individual gets its own carapace colour from a single shared geometry. */
    float k = clamp((base.r - uShellA.r) / max(uShellB.r - uShellA.r, 1e-4), 0.0, 1.0);
    base = mix(uShellA, uShellB, k * uStreak);
  }
  base *= vShade;

  float ndl = dot(N, L) * 0.5 + 0.5;
  float shade = mix(1.0, shadowAt(vWorldPos, N, dot(N, L)), uShadowStrength);
  vec3 ambient = mix(uAmbientBottom, uAmbientTop, N.y * 0.5 + 0.5);
  vec3 col = base * (ambient + uSunColor * (ndl * ndl) * shade);

  float up = smoothstep(0.12, 0.8, N.y);
  col += mix(base, vec3(1.0), 0.35) * uSunColor * (dapple(vWorldPos.xz, uTime) * up * 1.1 * shade);

  float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 3.0);
  col += uRimColor * (fres * 0.75);

  float fd = uFogDensity * length(cameraPosition - vWorldPos);
  col = mix(col, uFogColor, clamp(1.0 - exp(-fd * fd), 0.0, 1.0));

  gl_FragColor = vec4(col, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class TurtleMaterial extends THREE.ShaderMaterial {
  constructor(voxelSize: number, worldScale = 1) {
    super({
      name: 'TurtleMaterial',
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        // --- stroke. Every one is read by TURTLE_STROKE_GLSL.
        // uStroke is INTEGRATED on the CPU, never uTime times a live rate: scaling absolute
        // time makes the phase jump by uTime * deltaRate whenever the rate changes, and
        // uTime only grows.
        uStroke: { value: 0 },
        uSweep: { value: 0.62 },
        uTwist: { value: 0.60 },
        // The quarter-cycle lead of twist over sweep IS the thrust mechanism. At 0 the blade
        // is a flat plate flapping up and down, which produces essentially nothing.
        uLead: { value: -Math.PI / 2 },
        uFore: { value: 0.16 },
        uLag: { value: 0.95 },
        uBack: { value: 0.32 },
        uBank: { value: 0 },
        uFold: { value: 0 },
        uFLen: { value: 0.92 },
        uRLen: { value: 0.36 },
        uVox: { value: voxelSize },
        uDuneAmp: { value: OCEAN.duneAmplitude },
        uReefRise: { value: OCEAN.reefRise },
        uGround: { value: voxelSize * worldScale * 0.5 },

        // --- shell
        uSeam: { value: 0.55 },
        uStreak: { value: 0.62 },
        uShellA: { value: new THREE.Color(0.255, 0.205, 0.120) },
        uShellB: { value: new THREE.Color(0.560, 0.395, 0.175) },

        // --- the scene's own lighting vocabulary
        uTime: { value: 0 },
        uSunDir: {
          value: new THREE.Vector3(WATER.sunDir[0], WATER.sunDir[1], WATER.sunDir[2]).normalize(),
        },
        uSunColor: { value: srgb(WATER.sunColor) },
        uAmbientTop: { value: srgb(WATER.ambientTop) },
        uAmbientBottom: { value: srgb(WATER.ambientBottom) },
        uRimColor: { value: srgb(WATER.rimColor) },
        uFogColor: { value: srgb(WATER.fogColor) },
        uFogDensity: { value: WATER.fogDensity },
        uShadowMap: { value: null as THREE.Texture | null },
        uShadowMatrix: { value: new THREE.Matrix4() },
        uShadowStrength: { value: 0 },
      },
      side: THREE.FrontSide,
      transparent: false,
      fog: false,
    });
  }

  /** a depth twin for the shadow pass, sharing uniforms BY REFERENCE so the pose matches */
  makeShadowMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      name: 'TurtleMaterial:depth',
      vertexShader: VERT,
      fragmentShader: 'void main() { gl_FragColor = vec4(1.0); }',
      uniforms: this.uniforms,
      side: THREE.FrontSide,
      fog: false,
    });
  }

  /** the day cycle drives this material's lighting; see world/DayNight.ts */
  setEnv(env: SkyEnv): void {
    applyEnv(this.uniforms, env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    const u = this.uniforms;
    u.uShadowMap.value = map;
    (u.uShadowMatrix.value as THREE.Matrix4).copy(matrix);
    u.uShadowStrength.value = map === null ? 0 : strength;
  }
}
